import { parseEventLogs, type Address, type Log } from "viem";

// The two events NonfungiblePositionManager emits during mint() — ported
// from dotone-swap-v3's src/config/v3.ts V3_POSITION_MANAGER_ABI comment:
// "needed so PositionManager.interface.parseLog can decode mint receipts".
const MINT_EVENTS_ABI = [
  {
    type: "event",
    name: "Transfer",
    inputs: [
      { name: "from", type: "address", indexed: true },
      { name: "to", type: "address", indexed: true },
      { name: "tokenId", type: "uint256", indexed: true },
    ],
  },
  {
    type: "event",
    name: "IncreaseLiquidity",
    inputs: [
      { name: "tokenId", type: "uint256", indexed: true },
      { name: "liquidity", type: "uint128", indexed: false },
      { name: "amount0", type: "uint256", indexed: false },
      { name: "amount1", type: "uint256", indexed: false },
    ],
  },
] as const;

/**
 * Recovers the newly-minted position's tokenId from a mint transaction
 * receipt's logs — pure function, no RPC calls, unit-testable with a
 * recorded receipt fixture. Prefers IncreaseLiquidity (fires once, uniquely
 * for the new position) over Transfer (also fires for the mint's ERC20
 * transfers, filtered here to only the PositionManager's own address).
 */
export function extractMintedTokenId(logs: readonly Log[], positionManagerAddress: Address): bigint | null {
  const scoped = logs.filter((log) => log.address.toLowerCase() === positionManagerAddress.toLowerCase());
  const parsed = parseEventLogs({ abi: MINT_EVENTS_ABI, logs: scoped });

  const increase = parsed.find((event) => event.eventName === "IncreaseLiquidity");
  if (increase) return increase.args.tokenId;

  const transfer = parsed.find((event) => event.eventName === "Transfer");
  if (transfer) return transfer.args.tokenId;

  return null;
}
